import JSZip from "jszip";
import { saveAs } from "file-saver";

const INLINE_MARKDOWN_PATTERN =
  /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*/g;
const TABLE_SEPARATOR_CELL_PATTERN = /^:?-{3,}:?$/;

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeAttr = (value) => escapeHtml(value).replace(/"/g, "&quot;");

const escapeXml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

function splitTableRow(line) {
  const trimmed = String(line || "").trim();
  if (!trimmed.startsWith("|") || !trimmed.endsWith("|")) {
    return null;
  }

  return trimmed
    .slice(1, -1)
    .split("|")
    .map((cell) => cell.trim());
}

function isTableSeparatorRow(line) {
  const cells = splitTableRow(line);
  return (
    Array.isArray(cells) &&
    cells.length > 0 &&
    cells.every((cell) => TABLE_SEPARATOR_CELL_PATTERN.test(cell))
  );
}

function isTableRow(line) {
  const cells = splitTableRow(line);
  return Array.isArray(cells) && cells.length >= 2;
}

function normalizeCollapsedTableRows(value) {
  const lines = String(value || "").split(/\r?\n/);

  return lines
    .map((line) => {
      if (!line.includes("|") || !/\|\s*:?-{3,}:?\s*\|/.test(line)) {
        return line;
      }

      return line
        .replace(/([^\s|])\s+(\|[^|\n]+(?:\|[^|\n]+){1,}\|)/g, "$1\n$2")
        .replace(/\|\s+(?=\|)/g, "|\n");
    })
    .join("\n");
}

function stripInlineMarkdown(text) {
  const content = String(text || "");
  return content.replace(INLINE_MARKDOWN_PATTERN, (match, linkText, url, strong, code, em) => {
    if (linkText && url) return `${linkText} (${url})`;
    return strong || code || em || match;
  });
}

function inlineMarkdownToHtml(text) {
  const content = String(text || "");
  const tokenPattern = new RegExp(INLINE_MARKDOWN_PATTERN.source, "g");
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = tokenPattern.exec(content)) !== null) {
    const fullMatch = match[0];
    const start = match.index;
    const end = start + fullMatch.length;

    if (start > lastIndex) {
      parts.push(escapeHtml(content.slice(lastIndex, start)));
    }

    if (match[1] && match[2]) {
      parts.push(
        `<a href="${escapeAttr(match[2])}" target="_blank" rel="noreferrer">${escapeHtml(
          match[1],
        )}</a>`,
      );
    } else if (match[3]) {
      parts.push(`<strong>${escapeHtml(match[3])}</strong>`);
    } else if (match[4]) {
      parts.push(`<code>${escapeHtml(match[4])}</code>`);
    } else if (match[5]) {
      parts.push(`<em>${escapeHtml(match[5])}</em>`);
    } else {
      parts.push(escapeHtml(fullMatch));
    }

    lastIndex = end;
  }

  if (lastIndex < content.length) {
    parts.push(escapeHtml(content.slice(lastIndex)));
  }

  return parts.join("");
}

export function parsePaperAnalysisMarkdown(content) {
  const value = normalizeCollapsedTableRows(content).trim();
  if (!value) return [];

  const lines = value.split(/\r?\n/);
  const blocks = [];
  let paragraphBuffer = [];
  let listBuffer = [];
  let listType = null;
  let orderedListStart = 1;
  let tableBuffer = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return;
    const text = paragraphBuffer.join(" ").trim();
    paragraphBuffer = [];
    if (text) {
      blocks.push({ type: "paragraph", text });
    }
  };

  const flushList = () => {
    if (listBuffer.length === 0 || !listType) return;
    blocks.push({
      type: "list",
      ordered: listType === "ol",
      start: orderedListStart,
      items: [...listBuffer],
    });
    listBuffer = [];
    listType = null;
    orderedListStart = 1;
  };

  const flushTable = () => {
    if (tableBuffer.length < 2) {
      tableBuffer.forEach((row) => paragraphBuffer.push(row));
      tableBuffer = [];
      return;
    }

    const [headerLine, separatorLine, ...bodyLines] = tableBuffer;
    if (!isTableSeparatorRow(separatorLine)) {
      tableBuffer.forEach((row) => paragraphBuffer.push(row));
      tableBuffer = [];
      return;
    }

    const headers = splitTableRow(headerLine) || [];
    const rows = bodyLines
      .map((line) => splitTableRow(line))
      .filter((row) => Array.isArray(row) && row.length > 0);

    blocks.push({ type: "table", headers, rows });
    tableBuffer = [];
  };

  lines.forEach((rawLine) => {
    const trimmed = String(rawLine || "").trim();

    if (!trimmed) {
      flushTable();
      flushParagraph();
      flushList();
      return;
    }

    if (isTableRow(trimmed)) {
      flushParagraph();
      flushList();
      tableBuffer.push(trimmed);
      return;
    }

    flushTable();

    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        text: headingMatch[2],
      });
      return;
    }

    const unorderedMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (unorderedMatch) {
      flushParagraph();
      if (listType && listType !== "ul") {
        flushList();
      }
      listType = "ul";
      listBuffer.push(unorderedMatch[1]);
      return;
    }

    const orderedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph();
      if (listType && listType !== "ol") {
        flushList();
      }
      if (!listType) {
        orderedListStart = Number.parseInt(orderedMatch[1], 10);
      }
      listType = "ol";
      listBuffer.push(orderedMatch[2]);
      return;
    }

    flushList();
    paragraphBuffer.push(trimmed);
  });

  flushTable();
  flushParagraph();
  flushList();

  return blocks;
}

export function paperAnalysisMarkdownToPlainText(content) {
  return parsePaperAnalysisMarkdown(content)
    .map((block) => {
      if (block.type === "heading") return stripInlineMarkdown(block.text);
      if (block.type === "paragraph") return stripInlineMarkdown(block.text);
      if (block.type === "list") {
        return block.items
          .map((item, index) => {
            const prefix = block.ordered ? `${block.start + index}. ` : "- ";
            return `${prefix}${stripInlineMarkdown(item)}`;
          })
          .join("\n");
      }
      if (block.type === "table") {
        const rows = [block.headers, ...block.rows];
        return rows
          .map((row) => row.map((cell) => stripInlineMarkdown(cell)).join("\t"))
          .join("\n");
      }
      return "";
    })
    .filter(Boolean)
    .join("\n\n");
}

export function paperAnalysisMarkdownToHtml(content) {
  const blocks = parsePaperAnalysisMarkdown(content);
  const body = blocks
    .map((block) => {
      if (block.type === "heading") {
        const level = Math.min(Math.max(block.level + 1, 2), 4);
        return `<h${level}>${inlineMarkdownToHtml(block.text)}</h${level}>`;
      }
      if (block.type === "paragraph") {
        return `<p>${inlineMarkdownToHtml(block.text)}</p>`;
      }
      if (block.type === "list") {
        const tag = block.ordered ? "ol" : "ul";
        const startAttr = block.ordered && block.start > 1 ? ` start="${block.start}"` : "";
        const items = block.items
          .map((item) => `<li>${inlineMarkdownToHtml(item)}</li>`)
          .join("");
        return `<${tag}${startAttr}>${items}</${tag}>`;
      }
      if (block.type === "table") {
        const headers = block.headers
          .map((cell) => `<th>${inlineMarkdownToHtml(cell)}</th>`)
          .join("");
        const rows = block.rows
          .map(
            (row) =>
              `<tr>${block.headers
                .map((_, index) => `<td>${inlineMarkdownToHtml(row[index] || "")}</td>`)
                .join("")}</tr>`,
          )
          .join("");
        return `<table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
      }
      return "";
    })
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"><style>
body{font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.55;color:#111827;}
h2,h3,h4{margin:16px 0 8px;font-weight:700;color:#0f172a;}
p{margin:0 0 12px;}
ul,ol{margin:0 0 12px 22px;padding:0;}
li{margin:4px 0;}
table{border-collapse:collapse;margin:12px 0;width:100%;}
th,td{border:1px solid #cbd5e1;padding:8px;text-align:left;vertical-align:top;}
th{background:#f8fafc;font-weight:700;}
code{background:#f1f5f9;padding:1px 4px;border-radius:3px;}
a{color:#2563eb;}
</style></head><body>${body}</body></html>`;
}

function wordParagraph(text, options = {}) {
  const style = options.heading ? `<w:pStyle w:val="Heading${options.heading}"/>` : "";
  const bold = options.bold ? "<w:b/>" : "";
  return `<w:p><w:pPr>${style}</w:pPr><w:r><w:rPr>${bold}</w:rPr><w:t xml:space="preserve">${escapeXml(
    stripInlineMarkdown(text),
  )}</w:t></w:r></w:p>`;
}

function wordTable(block) {
  const tableRows = [block.headers, ...block.rows]
    .map(
      (row) =>
        `<w:tr>${block.headers
          .map(
            (_, index) =>
              `<w:tc><w:tcPr><w:tcW w:w="2400" w:type="dxa"/></w:tcPr>${wordParagraph(
                row[index] || "",
                { bold: row === block.headers },
              )}</w:tc>`,
          )
          .join("")}</w:tr>`,
    )
    .join("");

  return `<w:tbl><w:tblPr><w:tblW w:w="0" w:type="auto"/><w:tblBorders><w:top w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/><w:left w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/><w:bottom w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/><w:right w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/><w:insideH w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/><w:insideV w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/></w:tblBorders></w:tblPr>${tableRows}</w:tbl>`;
}

function buildDocumentXml(content, title) {
  const titleXml = title ? wordParagraph(title, { heading: 1 }) : "";
  const bodyXml = parsePaperAnalysisMarkdown(content)
    .map((block) => {
      if (block.type === "heading") {
        return wordParagraph(block.text, { heading: Math.min(block.level + 1, 3) });
      }
      if (block.type === "paragraph") {
        return wordParagraph(block.text);
      }
      if (block.type === "list") {
        return block.items
          .map((item, index) => {
            const prefix = block.ordered ? `${block.start + index}. ` : "\u2022 ";
            return wordParagraph(`${prefix}${item}`);
          })
          .join("");
      }
      if (block.type === "table") {
        return wordTable(block);
      }
      return "";
    })
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>${titleXml}${bodyXml}<w:sectPr><w:pgSz w:w="12240" w:h="15840"/><w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/></w:sectPr></w:body>
</w:document>`;
}

function buildStylesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:rPr><w:sz w:val="22"/><w:szCs w:val="22"/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:rPr><w:b/><w:sz w:val="32"/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Heading2"><w:name w:val="heading 2"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:rPr><w:b/><w:sz w:val="26"/></w:rPr></w:style>
  <w:style w:type="paragraph" w:styleId="Heading3"><w:name w:val="heading 3"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:rPr><w:b/><w:sz w:val="24"/></w:rPr></w:style>
</w:styles>`;
}

async function buildDocxBlob({ content, title }) {
  const zip = new JSZip();
  zip.file(
    "[Content_Types].xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
</Types>`,
  );
  zip.folder("_rels").file(
    ".rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
  );
  zip.folder("word").file("document.xml", buildDocumentXml(content, title));
  zip.folder("word").file("styles.xml", buildStylesXml());
  zip.folder("word").folder("_rels").file(
    "document.xml.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
  );

  return zip.generateAsync({
    type: "blob",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}

export async function copyPaperAnalysisMessageToClipboard(content) {
  const plainText = paperAnalysisMarkdownToPlainText(content);
  if (!plainText) {
    throw new Error("Cannot copy an empty paper analysis message.");
  }
  if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
    throw new Error("Rich clipboard export is not supported in this browser.");
  }

  const html = paperAnalysisMarkdownToHtml(content);
  await navigator.clipboard.write([
    new ClipboardItem({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([plainText], { type: "text/plain" }),
    }),
  ]);
}

export async function exportPaperAnalysisMessageToDocx({
  content,
  filename,
  title,
}) {
  const plainText = paperAnalysisMarkdownToPlainText(content);
  if (!plainText) {
    throw new Error("Cannot export an empty paper analysis message.");
  }

  const blob = await buildDocxBlob({ content, title });
  saveAs(blob, filename);
}

export function buildPaperAnalysisExportFilename(date = new Date()) {
  const timestamp = date
    .toISOString()
    .replace(/\.\d{3}Z$/, "")
    .replace(/[-:]/g, "")
    .replace("T", "-");
  return `medilens-paper-analysis-${timestamp}.docx`;
}
